import { useEffect, useState } from 'react';
import { waitForStyleOverrideFontResources } from '@/lib/embed/styleFontResources';

export type FontsReadyState = 'pending' | 'ready' | 'fallback';

export function useFontsReadyState(fontFamilies: readonly string[] = []): FontsReadyState {
  const [state, setState] = useState<FontsReadyState>('pending');
  const familyKey = fontFamilies.join('|');
  const [resolvedFamilyKey, setResolvedFamilyKey] = useState(familyKey);

  useEffect(() => {
    let didCancel = false;
    let settled = false;
    setResolvedFamilyKey(familyKey);
    setState('pending');

    // SSR guard (shouldn't happen since this is a hook, but keep it defensive)
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      setState('ready');
      return () => {
        didCancel = true;
      };
    }

    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    const hasFontsApi = !!fonts && typeof fonts.ready?.then === 'function';
    const maxWaitMs = hasFontsApi ? 2000 : 100;
    const setStateSafe = (next: FontsReadyState) => {
      if (didCancel || settled) return;
      settled = true;
      setState(next);
    };

    // Always enforce a max wait to avoid indefinite blocking.
    const timeoutId = window.setTimeout(() => setStateSafe('fallback'), maxWaitMs);

    if (!hasFontsApi) {
      return () => {
        didCancel = true;
        window.clearTimeout(timeoutId);
      };
    }

    // The Quiz style owner injects @font-face and Google CSS in its effect.
    // Start after that effect has committed so `fonts.load` observes the exact
    // selected resources rather than settling against the previous document.
    const startFontLoadsId = window.setTimeout(() => {
      const requestedFamilies = [
        ...new Set(
          familyKey
            .split('|')
            .map((family) => family.trim())
            .filter(Boolean)
        ),
      ];
      const readiness = requestedFamilies.length
        ? waitForStyleOverrideFontResources().then(() =>
            Promise.all(requestedFamilies.map((family) => fonts.load(`1em "${family}"`)))
          )
        : fonts.ready;
      readiness
        .then(() => {
          window.clearTimeout(timeoutId);
          setStateSafe('ready');
        })
        .catch(() => {
          window.clearTimeout(timeoutId);
          setStateSafe('fallback');
        });
    }, 0);

    return () => {
      didCancel = true;
      window.clearTimeout(timeoutId);
      window.clearTimeout(startFontLoadsId);
    };
  }, [familyKey]);

  return resolvedFamilyKey === familyKey ? state : 'pending';
}

/**
 * Returns true when the current document fonts are ready. A bounded fallback
 * keeps callers terminal while `useFontsReadyState` lets Quiz Playback freeze
 * an explicit system-font decision when a selected font cannot be loaded.
 */
export function useFontsReady(): boolean {
  return useFontsReadyState() !== 'pending';
}
